import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormBuilder, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import * as lodash from 'lodash';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

export const LocalStorageKeys = {
  form: 'MONOT_FORM',
  formats: 'MONOT_FORMATS',
};

const columnRowSeparatorValidator: ValidatorFn = (group: AbstractControl):  ValidationErrors | null => {
  let columnSeparator = group.get('columnSeparator').value;
  let rowSeparator = group.get('rowSeparator').value
  return columnSeparator === rowSeparator ? {separatorsAreSame: true} : null;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy, OnDestroy {
  private readonly destroy$ = new Subject();
  private readonly defaultInput = `id: number;
firstName: string;
lastName: string;
email: string;
  `;
  private readonly defaultFormat = `with{{lodash($0).upperFirst()}}($0: {{$1.replace(';','')}}): TestBuilder {
  this._$0 = $0;
  return this;
}
  `;
  form = this.fb.group({
    input: [null],
    columnSeparator: [':'],
    rowSeparator: ['\\n'],
    format: [null],
    output: [null],
  }, {
    validators: columnRowSeparatorValidator
  });
  editorOptions = { theme: 'vs-light', language: 'text/plain' };
  showAbout = false;
  showInputHelp = false;
  showFormatHelp = false;
  showFormatHistory = false;

  input$ = this.form.get('input').valueChanges;
  format$ = this.form.get('format').valueChanges;
  columnSeparator$ = this.form.get('columnSeparator').valueChanges.pipe(
    startWith(':'),
    map((x) => this.interpret(x))
    );
  rowSeparator$ = this.form.get('rowSeparator').valueChanges.pipe(
    startWith('\\n'),
    map((x) => this.interpret(x))
  );

  rows$ = this.input$.pipe(
    filter((x) => Boolean(x)),
    withLatestFrom(this.rowSeparator$),
    map(([val, rowSep]) => val.trim().split(rowSep))
  );

  output$ = combineLatest([
    this.rows$,
    this.format$,
    this.columnSeparator$,
  ]).pipe(
    filter(() => this.form.valid),
    map(([rows, format, colSep]) => {
      const outputtedRows = [];
      rows.forEach((row) => {
        // remove ending new line/break which breaks the evaluation
        row = row.replace('\r', '').replace('\n', '');
        const cols = row.split(colSep);
        let output = format;
        const regex = new RegExp(/{{([^}]+)}}/, 'g');
        // replace interpolation
        cols.forEach((col, i) => {
          // reverse column number so that e.g. $1 doesnt get replaced in $10
          const reversedColNumber = Math.abs(i - (cols.length - 1));
          const trimmedCol = cols[reversedColNumber].trim();
          // For each column, replace the interpolation for that column with the evaluated stuff
          output = output.replace(regex, (pattern, match) => {
            let dollarRegex = new RegExp('\\$' + reversedColNumber, 'g');
            const isMatch = dollarRegex.test(match);
            // If the match contains a column number not equal to the current one, return the current match but surrounded with braces again
            if (!isMatch) return `{{${match}}}`;
            let replaced = match.replace(dollarRegex, `"${trimmedCol}"`);
            try {
              replaced = this.evaluateString(replaced.trim());
            } catch (err) {
              replaced = cols[reversedColNumber] ?? '';
            }
            return replaced;
          });
        });
        // replace regular vars
        cols.forEach((col, i) => {
          // reverse column number so that e.g. $1 doesnt get replaced in $10
          const reversedColNumber = Math.abs(i - (cols.length - 1));
          const trimmedCol = cols[reversedColNumber].trim();
          let dollarRegex = new RegExp('\\$' + reversedColNumber, 'g');
          output = output.replace(dollarRegex, `${trimmedCol}`);
        });
        outputtedRows.push(output);
      });
      return outputtedRows;
    }),
    map((x) => x.join('\n')),
    tap((x) => {
      this.form.controls.output.setValue(x);
      this.cdr.detectChanges();
    }),
    takeUntil(this.destroy$)
  );

  formats$ = of(localStorage.getItem(LocalStorageKeys.formats)).pipe(
    filter(x => Boolean(x)),
    map(x => JSON.parse(x) as string[])
  );


  constructor(
    private readonly fb: FormBuilder,
    public sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.output$.subscribe();
    const fromCache = localStorage.getItem(LocalStorageKeys.form);
    if (fromCache) {
      let currentForm = JSON.parse(fromCache);
      this.form.patchValue(currentForm);
      this.form.controls.input.updateValueAndValidity();
      this.form.controls.format.updateValueAndValidity();
      this.form.controls.columnSeparator.updateValueAndValidity();
    }
    else {
      this.form.controls.input.setValue(this.defaultInput);
      this.form.controls.format.setValue(this.defaultFormat);
    }
  }

  ngOnDestroy(): void {
    // this.saveFormToLocalStorage();
    this.destroy$.next();
    this.destroy$.complete();
  }

  interpret(input) {
    //convert the literal string '\r' into a carriage return (\r). ditto for '\n' and '\t'
    return input
      .replace(/\\r/g, '\r')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t');
  }

  unterpret(input) {
    //convert a carriage return into a literal string '\r'. ditto for linefeed=>'\n' and tab=>'\t'
    return input
      .replace(/\r/g, '\\r')
      .replace(/\n/g, '\\n')
      .replace(/\t/g, '\\t');
  }

  selectFormatFromHistory(format: string): void {
    this.form.controls.format.setValue(format);
    this.showFormatHistory = false;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler(event) {
    const value = this.form.value;
    if(value.input.trim() || value.format.trim()){
      this.saveFormToLocalStorage();
      this.addCurrentFormatToLocalStorage();
    }
  }

  private evaluateString(str: string) {
    return Function('lodash', '"use strict";return (' + str + ')')(lodash);
  }

  private saveFormToLocalStorage() {
    const value = this.form.value;
    let currentForm = {
      input: value.input,
      format: value.format,
      columnSeparator: value.columnSeparator,
      rowSeparator: value.rowSeparator,
    };

    localStorage.setItem(LocalStorageKeys.form, JSON.stringify(currentForm));
  }

  private addCurrentFormatToLocalStorage() {
    const currentFormat = this.form.value.format.trim();
    if(!currentFormat) return;
    const fromCache = localStorage.getItem(LocalStorageKeys.formats);
    let formats = [];
    if (fromCache) {
      formats = JSON.parse(fromCache);
      if (formats?.length) {
        if (!formats.find((x) => x === currentFormat))
          formats.push(currentFormat);
      } else formats = [];
    } else {
      formats = [currentFormat];
    }

    localStorage.setItem(LocalStorageKeys.formats, JSON.stringify(formats));
  }
}
